import { useState } from "react";
import type { SecureGroupView } from "../api";

/* ═══════════════════════════════════════════════════════════════════════
   The locked box.

   crAIg asks for a private detail in the flow of conversation, and the
   composer becomes this instead of a chat reply. It reads as one
   continuous conversation, which is the point — being sent to a separate
   panel mid-sentence is where people lose the thread.

   The security property is the thing to preserve: what is typed here goes
   straight to the server's secure endpoint. It never enters the message
   thread, is never sent to the model, and never comes back. crAIg learns
   only that the field was filled.
   ═══════════════════════════════════════════════════════════════════════ */

const digits = (s: string) => s.replace(/\D/g, "");

/**
 * Format checks, mirrored from the pack.
 *
 * This duplicates logic the server has, because the pack's own checks are
 * functions and don't survive serialisation. It earns the duplication:
 * catching a mistyped account number while the customer is still looking
 * at it is worth far more than catching it after a refund has gone
 * somewhere else. These are WARNINGS — nothing blocks a save, because
 * people do have unusual details and a human verifies anyway.
 */
function warn(key: string, value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  const d = digits(v);

  if (/tax file number/i.test(key)) {
    if (d.length !== 9) return `That's ${d.length} digits — a TFN has 9.`;
    const w = [1, 4, 3, 7, 5, 8, 6, 9, 10];
    const sum = d.split("").reduce((n, c, i) => n + Number(c) * w[i]!, 0);
    return sum % 11 === 0 ? null : "That doesn't look quite right — worth double-checking.";
  }
  if (/^abn$/i.test(key)) {
    if (d.length !== 11) return `That's ${d.length} digits — an ABN has 11.`;
    const w = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
    const n = d.split("").map(Number);
    n[0] = n[0]! - 1;
    return n.reduce((t, x, i) => t + x * w[i]!, 0) % 89 === 0
      ? null
      : "That doesn't look quite right — worth double-checking.";
  }
  if (/^bsb$/i.test(key)) return d.length === 6 ? null : `A BSB is 6 digits — you've got ${d.length}.`;
  if (/account number/i.test(key)) {
    return d.length >= 6 && d.length <= 10
      ? null
      : "Account numbers are usually 6–10 digits — please check.";
  }
  if (/expiry/i.test(key)) {
    const when = new Date(v);
    if (Number.isNaN(when.getTime())) return null;
    return when < new Date() ? "That's expired — a current one will be needed." : null;
  }
  return null;
}

export function SecureInline({
  group,
  reviewerNoun,
  onSave,
  onSkip,
  saving,
}: {
  group: SecureGroupView;
  reviewerNoun: string;
  onSave(values: Record<string, string>): void;
  onSkip(): void;
  saving?: boolean;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState(false);

  const complete = group.fields.every((f) => (values[f.key] ?? "").trim());
  const warnings = group.fields.map((f) => warn(f.key, values[f.key] ?? ""));
  const anyWarning = warnings.some(Boolean);

  return (
    <div
      className="fade-up"
      style={{
        border: "1px solid var(--accent-edge)",
        background: "var(--accent-tint)",
        borderRadius: "var(--radius)",
        padding: "0.85rem",
      }}
    >
      <div className="flex items-center gap-2 mb-1">
        <span aria-hidden="true">🔒</span>
        <span style={{ fontWeight: 650, fontSize: 14 }}>{group.title}</span>
        <span className="ml-auto" style={{ fontSize: 11.5, color: "var(--ink-3)" }}>
          crAIg can't see this
        </span>
      </div>

      <p style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: "0.65rem" }}>{group.why}</p>

      <div className="space-y-2.5">
        {group.fields.map((f, i) => (
          <div key={f.key}>
            <label
              htmlFor={`secure-${f.key}`}
              className="block mb-1"
              style={{ fontSize: 12, fontWeight: 550, color: "var(--ink-2)" }}
            >
              {f.key}
            </label>
            <input
              id={`secure-${f.key}`}
              value={values[f.key] ?? ""}
              onChange={(e) => {
                setValues((v) => ({ ...v, [f.key]: e.target.value }));
                setTouched(true);
              }}
              placeholder={f.placeholder}
              type={f.type === "date" ? "date" : "text"}
              // Nothing here should be remembered, suggested or corrected.
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              className="field"
              style={warnings[i] && touched ? { borderColor: "var(--warn)" } : undefined}
            />
            {warnings[i] && touched && (
              <p style={{ fontSize: 12, color: "var(--warn)", marginTop: 4 }}>{warnings[i]}</p>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-3">
        <button
          type="button"
          onClick={() => onSave(values)}
          disabled={!complete || saving}
          className="btn btn-primary"
        >
          {saving ? "Saving…" : anyWarning ? "Save anyway" : "Save"}
        </button>
        <button
          type="button"
          onClick={onSkip}
          style={{
            fontSize: 13,
            color: "var(--ink-3)",
            background: "none",
            border: "none",
            cursor: "pointer",
            textDecoration: "underline",
            textUnderlineOffset: 3,
          }}
        >
          I'll do this later
        </button>
      </div>

      <p style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: "0.6rem" }}>
        This never appears in the chat and crAIg never sees it — it goes straight to your{" "}
        {reviewerNoun}.
      </p>
    </div>
  );
}
